import * as React from 'react';
import { AuthContext } from '../../AuthService';
import { NotificationModal } from '../../components/NotificationModal';
import * as Sentry from '@sentry/browser';
import { LoginForm } from './LoginForm';
import { MFAForm } from './MFAForm';
import './Login.scss';
import { useLogin } from '../../hooks/useLogin';

export const Login = ({ history }) => {
  const auth = React.useContext(AuthContext);
  const [login] = useLogin();
  const [error, setError] = React.useState<Error | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [mfaCodes, setMFACodes] = React.useState<null | { mfaToken: string; oobCode: string }>(null);

  const onSubmit = React.useCallback(
    async ({ email, password }) => {
      setLoading(true);
      try {
        const tokens = await auth.login(email, password);
        await login(tokens.accessToken);
        history.push('/home');
      } catch (error) {
        setLoading(false);
        if (error.isMFAError) {
          setMFACodes({
            mfaToken: error.mfaToken,
            oobCode: error.oobCode,
          });
          return;
        }

        let exceptionId;
        if (error.original) {
          Sentry.withScope((scope) => {
            scope.setExtras(error);
            exceptionId = Sentry.captureException(error.original);
          });
        } else {
          exceptionId = Sentry.captureException(error);
        }

        setError(new Error(`${error.description || error.messsage || 'Unknown Error'} (${exceptionId})`));
        return;
      }
    },
    [history, auth, login]
  );

  const submitMFACode = React.useCallback(
    async ({ mfaCode }) => {
      setLoading(true);
      try {
        await auth.loginWithMFACode(mfaCodes.mfaToken, mfaCode, mfaCodes.oobCode);

        history.push('/home');
      } catch (error) {
        setError(error);
        setLoading(false);
      }
    },
    [history, mfaCodes, auth]
  );

  const [showModal, setShowModal] = React.useState<boolean>(false);

  const onReport = React.useCallback(() => {
    const reportData = {
      permissions: auth.getPermissions(),
      cookies: auth.cookies.getAll(),
      accessTokenInfo: auth.getAccessTokenInfo(),
      fullUrl: window.location.href,
    };

    Sentry.withScope((scope) => {
      scope.setExtras(reportData);
      Sentry.captureException(new Error('Auth Report'));
    });

    setShowModal(true);
  }, [auth]);

  return (
    <div>
      <div className='centered-block login-card'>
        {!mfaCodes ? (
          <LoginForm login={onSubmit} error={error} loading={loading} onReport={onReport} />
        ) : (
          <MFAForm login={submitMFACode} error={error} loading={loading} />
        )}
      </div>
      <div className='login-version'>{process.env.RAZZLE_NOBLE_COMMIT_HASH}</div>
      <NotificationModal text='Report Sent!' onClick={() => setShowModal(false)} hidden={!showModal} />
    </div>
  );
};
